import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import {
  PENDING_LABEL,
  VERIFICATION_INFO_PATH,
  VERIFIED_COMPACT_LABEL,
  VERIFIED_LABEL,
  getDealerVerificationLabel,
  getDealerVerificationPublicState,
  getDealerVerificationTooltip,
  trackDealerVerificationEvent,
} from "../../utils/dealerVerificationDisplay";
import { useOptionalLocale } from "../../intl";
import "../../styles/dealerVerificationBadge.css";

export default function DealerVerificationBadge({
  dealer,
  listing,
  verificationStatus,
  dealerStatus,
  dealerVerified,
  publicProfileEnabled,
  size = "medium",
  variant = "default",
  showTooltip = true,
  showLabel = true,
  compact = false,
  context = "default",
  showInfoLink = false,
  className = "",
}) {
  const tooltipId = useId();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const localeContext = useOptionalLocale();
  const t = localeContext?.t;

  const subject = dealer || listing || {
    verificationStatus,
    status: dealerStatus,
    publicProfileEnabled,
    dealerVerified,
    dealerVerificationStatus: verificationStatus,
    dealerStatus,
    dealerPublicProfileEnabled: publicProfileEnabled,
  };

  const publicState = getDealerVerificationPublicState(subject);
  const showVerified = publicState.showBadge || dealerVerified === true;
  const showPending = publicState.showPending && context === "profile";

  useEffect(() => {
    if (showVerified || showPending) {
      trackDealerVerificationEvent("dealer_verification_badge_viewed", {
        state: showVerified ? "verified" : "pending",
        context,
        variant,
      });
    }
  }, [showVerified, showPending, context, variant]);

  if (!showVerified && !showPending) return null;

  const isPending = showPending && !showVerified;
  const isCompactLabel = compact || variant === "compact" || variant === "card";
  const label = isPending
    ? t?.("automotive.badges.dealerPending") || PENDING_LABEL
    : showVerified && t
      ? isCompactLabel
        ? t("automotive.badges.dealerVerified")
        : t("automotive.badges.dealerVerifiedFull")
      : getDealerVerificationLabel(subject, isCompactLabel ? "compact" : context);

  const tooltip = showTooltip ? getDealerVerificationTooltip(subject) : null;
  const ariaLabel = isPending ? PENDING_LABEL : label || VERIFIED_LABEL;

  const classes = [
    "dealer-verification-badge",
    `dealer-verification-badge--${size}`,
    `dealer-verification-badge--${variant}`,
    isPending ? "dealer-verification-badge--pending" : "dealer-verification-badge--verified",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const toggleTooltip = () => {
    if (!tooltip) return;
    setTooltipOpen((open) => {
      const next = !open;
      if (next) {
        trackDealerVerificationEvent("dealer_verification_tooltip_opened", { context, variant });
      }
      return next;
    });
  };

  return (
    <span className={classes}>
      <span
        className="dealer-verification-badge__pill"
        aria-label={ariaLabel}
        title={!showTooltip || tooltipOpen ? undefined : tooltip}
        aria-describedby={tooltip && tooltipOpen ? tooltipId : undefined}
      >
        <span className="dealer-verification-badge__icon" aria-hidden="true">
          {isPending ? "◷" : "✓"}
        </span>
        {showLabel && label ? (
          <span className="dealer-verification-badge__label">{label}</span>
        ) : null}
        {tooltip && showTooltip ? (
          <button
            type="button"
            className="dealer-verification-badge__info"
            aria-label={`Más información: ${ariaLabel}`}
            aria-expanded={tooltipOpen}
            aria-controls={tooltipId}
            onClick={toggleTooltip}
          >
            i
          </button>
        ) : null}
      </span>
      {tooltip && tooltipOpen ? (
        <span id={tooltipId} role="tooltip" className="dealer-verification-badge__tooltip">
          {tooltip}
          {showInfoLink ? (
            <>
              {" "}
              <Link
                to={VERIFICATION_INFO_PATH}
                onClick={() =>
                  trackDealerVerificationEvent("dealer_verification_info_clicked", { context })
                }
              >
                Más información
              </Link>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
